import React, { useState, useEffect } from "react";
import api from "@/services/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Users,
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  UserPlus,
  Phone,
  Mail,
  MapPin,
  FileText,
  Smartphone,
  ShoppingCart,
  Truck,
  Calendar,
  Search,
  X,
  Copy,
} from "lucide-react";

export default function AdminSolicitudes() {
  const [solicitudes, setSolicitudes] = useState([]);
  const [resumen, setResumen] = useState({ total: 0, pendientes: 0, aprobadas: 0, rechazadas: 0 });
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState("pendiente");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSolicitud, setSelectedSolicitud] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [approvalResult, setApprovalResult] = useState(null);
  
  const [approvalForm, setApprovalForm] = useState({
    zona: "",
    meta_diaria: 150000,
    meta_diaria_comision: 15000,
    porcentaje_comision: 10,
    apoyo_transporte: 5000,
  });
  
  const [rejectForm, setRejectForm] = useState({
    motivo: "",
  });

  useEffect(() => {
    fetchSolicitudes();
  }, [filtro]);

  const fetchSolicitudes = async () => {
    try {
      const params = filtro !== "todas" ? `?estado=${filtro}` : "";
      const res = await api.get(`/solicitudes${params}`);
      setSolicitudes(res.data.solicitudes);
      setResumen(res.data.resumen);
    } catch (error) {
      toast.error("Error al cargar solicitudes");
    } finally {
      setLoading(false);
    }
  };

  const handleAprobar = async () => {
    if (!selectedSolicitud) return;
    setProcessing(true);
    try {
      const res = await api.post(`/solicitudes/${selectedSolicitud.id}/aprobar`, approvalForm);
      setApprovalResult(res.data);
      toast.success("Solicitud aprobada exitosamente");
      fetchSolicitudes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al aprobar solicitud");
    } finally {
      setProcessing(false);
    }
  };

  const handleRechazar = async () => {
    if (!selectedSolicitud) return;
    setProcessing(true);
    try {
      await api.post(`/solicitudes/${selectedSolicitud.id}/rechazar`, rejectForm);
      toast.success("Solicitud rechazada");
      setShowRejectModal(false);
      setSelectedSolicitud(null);
      fetchSolicitudes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al rechazar solicitud");
    } finally {
      setProcessing(false);
    }
  };

  const openApprovalModal = (solicitud) => {
    setSelectedSolicitud(solicitud);
    setApprovalForm({
      zona: "",
      meta_diaria: 150000,
      meta_diaria_comision: 15000,
      apoyo_transporte: solicitud.tipo_vendedor === "carrito" ? 10000 : 5000,
    });
    setApprovalResult(null);
    setShowApprovalModal(true);
  };

  const openRejectModal = (solicitud) => {
    setSelectedSolicitud(solicitud);
    setRejectForm({ motivo: "" });
    setShowRejectModal(true);
  };

  const closeModals = () => {
    setShowApprovalModal(false);
    setShowRejectModal(false);
    setSelectedSolicitud(null);
    setApprovalResult(null);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copiado al portapapeles");
  };

  const filteredSolicitudes = solicitudes.filter(
    (s) =>
      s.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.documento_id.includes(searchTerm)
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("es-CO", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
    }).format(value);
  };

  const getTipoDocLabel = (tipo) => {
    const labels = { cc: "C.C.", ce: "C.E.", pasaporte: "Pasaporte", nit: "NIT" };
    return labels[tipo] || tipo.toUpperCase();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-torta-brown">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-solicitudes">
      <div>
        <h1 className="font-display text-3xl text-torta-dark">Solicitudes de Registro</h1>
        <p className="text-torta-muted">Gestiona las solicitudes de nuevos vendedores</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-gray-600" />
            </div>
            <div>
              <p className="text-2xl font-display text-torta-dark">{resumen.total}</p>
              <p className="text-xs text-torta-muted">Total</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-display text-amber-600">{resumen.pendientes}</p>
              <p className="text-xs text-torta-muted">Pendientes</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-display text-green-600">{resumen.aprobadas}</p>
              <p className="text-xs text-torta-muted">Aprobadas</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <XCircle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-display text-red-600">{resumen.rechazadas}</p>
              <p className="text-xs text-torta-muted">Rechazadas</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 shadow-sm flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex flex-wrap gap-2">
          {[
            { value: "pendiente", label: "Pendientes", color: "amber" },
            { value: "aprobado", label: "Aprobadas", color: "green" },
            { value: "rechazado", label: "Rechazadas", color: "red" },
            { value: "todas", label: "Todas", color: "gray" },
          ].map((f) => (
            <button
              key={f.value}
              onClick={() => setFiltro(f.value)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                filtro === f.value
                  ? f.color === "amber"
                    ? "bg-amber-500 text-white"
                    : f.color === "green"
                    ? "bg-green-500 text-white"
                    : f.color === "red"
                    ? "bg-red-500 text-white"
                    : "bg-gray-600 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Buscar por nombre, email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {/* Solicitudes List */}
      <div className="space-y-3">
        {filteredSolicitudes.length === 0 ? (
          <div className="bg-white rounded-xl p-8 text-center">
            <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-torta-muted">No hay solicitudes {filtro !== "todas" ? filtro + "s" : ""}</p>
          </div>
        ) : (
          filteredSolicitudes.map((solicitud) => (
            <div
              key={solicitud.id}
              className="bg-white rounded-xl p-4 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4"
            >
              <div className="flex items-start gap-4">
                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    solicitud.tipo_vendedor === "digital"
                      ? "bg-blue-100 text-blue-600"
                      : "bg-amber-100 text-amber-600"
                  }`}
                >
                  {solicitud.tipo_vendedor === "digital" ? (
                    <Smartphone className="w-6 h-6" />
                  ) : (
                    <ShoppingCart className="w-6 h-6" />
                  )}
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-medium text-torta-dark">{solicitud.nombre}</h3>
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        solicitud.estado === "pendiente"
                          ? "bg-amber-100 text-amber-700"
                          : solicitud.estado === "aprobado"
                          ? "bg-green-100 text-green-700"
                          : "bg-red-100 text-red-700"
                      }`}
                    >
                      {solicitud.estado.charAt(0).toUpperCase() + solicitud.estado.slice(1)}
                    </span>
                  </div>
                  <p className="text-sm text-torta-muted flex items-center gap-2">
                    <Mail className="w-3 h-3" /> {solicitud.email}
                  </p>
                  <p className="text-sm text-torta-muted flex items-center gap-2">
                    <Phone className="w-3 h-3" /> {solicitud.telefono}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    <Calendar className="w-3 h-3 inline mr-1" />
                    Solicitado: {formatDate(solicitud.fecha_solicitud)}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 ml-auto">
                {solicitud.estado === "pendiente" && (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openRejectModal(solicitud)}
                      className="text-red-600 border-red-200 hover:bg-red-50"
                    >
                      <XCircle className="w-4 h-4 mr-1" />
                      Rechazar
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => openApprovalModal(solicitud)}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Aprobar
                    </Button>
                  </>
                )}
                {solicitud.estado !== "pendiente" && (
                  <span className="text-xs text-gray-400">
                    {solicitud.estado === "aprobado" ? "Aprobado" : "Rechazado"} por {solicitud.resuelto_por}
                    <br />
                    {formatDate(solicitud.fecha_resolucion)}
                  </span>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Approval Modal */}
      {showApprovalModal && selectedSolicitud && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <h2 className="font-display text-xl text-torta-dark flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-green-600" />
                Aprobar Solicitud
              </h2>
              <button onClick={closeModals} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Applicant Info */}
              <div className="bg-gray-50 rounded-xl p-4">
                <h3 className="font-medium text-torta-dark mb-3">Información del Solicitante</h3>
                <div className="grid sm:grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-torta-brown" />
                    <span className="text-torta-muted">Nombre:</span>
                    <span className="text-torta-dark font-medium">{selectedSolicitud.nombre}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-torta-brown" />
                    <span className="text-torta-muted">Email:</span>
                    <span className="text-torta-dark font-medium">{selectedSolicitud.email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-torta-brown" />
                    <span className="text-torta-muted">Teléfono:</span>
                    <span className="text-torta-dark font-medium">{selectedSolicitud.telefono}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-torta-brown" />
                    <span className="text-torta-muted">Documento:</span>
                    <span className="text-torta-dark font-medium">
                      {getTipoDocLabel(selectedSolicitud.tipo_documento)} {selectedSolicitud.documento_id}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 sm:col-span-2">
                    <MapPin className="w-4 h-4 text-torta-brown" />
                    <span className="text-torta-muted">Dirección:</span>
                    <span className="text-torta-dark font-medium">{selectedSolicitud.direccion}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {selectedSolicitud.tipo_vendedor === "digital" ? (
                      <Smartphone className="w-4 h-4 text-blue-600" />
                    ) : (
                      <ShoppingCart className="w-4 h-4 text-amber-600" />
                    )}
                    <span className="text-torta-muted">Tipo:</span>
                    <span
                      className={`font-medium ${
                        selectedSolicitud.tipo_vendedor === "digital" ? "text-blue-600" : "text-amber-600"
                      }`}
                    >
                      {selectedSolicitud.tipo_vendedor === "digital" ? "Digital" : "Carrito"}
                    </span>
                  </div>
                  {selectedSolicitud.tipo_vendedor === "carrito" && (
                    <div className="flex items-center gap-2">
                      <Truck className="w-4 h-4 text-torta-brown" />
                      <span className="text-torta-muted">Requiere carro:</span>
                      <span className="text-torta-dark font-medium">
                        {selectedSolicitud.requiere_carro ? "Sí" : "No"}
                      </span>
                    </div>
                  )}
                </div>
                {selectedSolicitud.notas && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <p className="text-sm text-torta-muted">
                      <strong>Notas:</strong> {selectedSolicitud.notas}
                    </p>
                  </div>
                )}
              </div>

              {/* Approval Result */}
              {approvalResult && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                  <h3 className="font-medium text-green-800 mb-3 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    ¡Usuario Creado Exitosamente!
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between bg-white rounded-lg p-3">
                      <div>
                        <p className="text-gray-500">Email de acceso</p>
                        <p className="font-mono font-medium">{approvalResult.email}</p>
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => copyToClipboard(approvalResult.email)}>
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="flex items-center justify-between bg-white rounded-lg p-3">
                      <div>
                        <p className="text-gray-500">Contraseña temporal</p>
                        <p className="font-mono font-medium">{approvalResult.password_temporal}</p>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => copyToClipboard(approvalResult.password_temporal)}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                    <p className="text-green-700 text-xs mt-2">
                      <strong>Nota:</strong> {approvalResult.nota}
                    </p>
                  </div>
                </div>
              )}

              {/* Assignment Form (only show if not approved yet) */}
              {!approvalResult && (
                <div className="space-y-4">
                  <h3 className="font-medium text-torta-dark">Asignar Configuración</h3>

                  <div className="space-y-2">
                    <Label>Zona asignada</Label>
                    <Input
                      value={approvalForm.zona}
                      onChange={(e) => setApprovalForm({ ...approvalForm, zona: e.target.value })}
                      placeholder="Ej: Chapinero, Usaquén, etc."
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Meta diaria ventas</Label>
                      <Input
                        type="number"
                        value={approvalForm.meta_diaria}
                        onChange={(e) =>
                          setApprovalForm({ ...approvalForm, meta_diaria: parseInt(e.target.value) || 0 })
                        }
                      />
                      <p className="text-xs text-gray-400">{formatCurrency(approvalForm.meta_diaria)}</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Meta diaria comisión</Label>
                      <Input
                        type="number"
                        value={approvalForm.meta_diaria_comision}
                        onChange={(e) =>
                          setApprovalForm({ ...approvalForm, meta_diaria_comision: parseInt(e.target.value) || 0 })
                        }
                      />
                      <p className="text-xs text-gray-400">{formatCurrency(approvalForm.meta_diaria_comision)}</p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Apoyo transporte semanal</Label>
                    <Input
                      type="number"
                      value={approvalForm.apoyo_transporte}
                      onChange={(e) =>
                        setApprovalForm({ ...approvalForm, apoyo_transporte: parseInt(e.target.value) || 0 })
                      }
                    />
                    <p className="text-xs text-gray-400">{formatCurrency(approvalForm.apoyo_transporte)}/semana</p>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-blue-800 text-sm">
                      <strong>Nota:</strong> El porcentaje de comisión se aplica según la configuración general del sistema.
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-100 flex justify-end gap-3">
              <Button variant="outline" onClick={closeModals}>
                {approvalResult ? "Cerrar" : "Cancelar"}
              </Button>
              {!approvalResult && (
                <Button onClick={handleAprobar} disabled={processing} className="bg-green-600 hover:bg-green-700">
                  {processing ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Procesando...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      Aprobar y Crear Usuario
                    </span>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && selectedSolicitud && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md">
            <div className="p-6 border-b border-gray-100">
              <h2 className="font-display text-xl text-torta-dark flex items-center gap-2">
                <XCircle className="w-5 h-5 text-red-600" />
                Rechazar Solicitud
              </h2>
            </div>

            <div className="p-6 space-y-4">
              <p className="text-torta-muted">
                ¿Estás seguro de rechazar la solicitud de <strong>{selectedSolicitud.nombre}</strong>?
              </p>

              <div className="space-y-2">
                <Label>Motivo del rechazo (opcional)</Label>
                <Textarea
                  value={rejectForm.motivo}
                  onChange={(e) => setRejectForm({ ...rejectForm, motivo: e.target.value })}
                  placeholder="Explica brevemente el motivo..."
                  rows={3}
                />
              </div>
            </div>

            <div className="p-6 border-t border-gray-100 flex justify-end gap-3">
              <Button variant="outline" onClick={closeModals}>
                Cancelar
              </Button>
              <Button onClick={handleRechazar} disabled={processing} className="bg-red-600 hover:bg-red-700">
                {processing ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Procesando...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <XCircle className="w-4 h-4" />
                    Rechazar Solicitud
                  </span>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
